export function TermsPage() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16 space-y-10">
      <div className="space-y-3">
        <h1 className="text-3xl font-bold tracking-tight">Terms of Service</h1>
        <p className="text-sm text-muted-foreground">Last updated: June 2026</p>
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          This document is a working draft pending review by legal counsel. It does not constitute
          legal advice and will be revised before public launch.
        </div>
      </div>

      <div className="space-y-8 text-sm leading-relaxed text-muted-foreground">

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">1. Agreement to terms</h2>
          <p>
            By accessing or using the Direct Campaign platform (&quot;the Service&quot;), you agree to be
            bound by these Terms of Service (&quot;Terms&quot;). If you are using the Service on behalf of a
            campaign, political committee, or other organization, you represent that you have the
            authority to bind that organization to these Terms, and &quot;you&quot; refers to both you
            individually and the organization.
          </p>
          <p>
            If you do not agree to these Terms, do not use the Service.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">2. Description of the Service</h2>
          <p>
            Direct Campaign provides a software platform for political campaigns and advocacy
            organizations, including tools for voter data management, walkbook creation, volunteer
            coordination, and canvassing program oversight. Access is granted to approved
            organizations through an invitation-based onboarding process.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">3. Accounts and access</h2>
          <p>
            Access to the platform is managed by an organization administrator. Administrators are
            responsible for:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Maintaining the confidentiality of account credentials</li>
            <li>Ensuring all users added to the organization agree to these Terms</li>
            <li>All activity that occurs under the organization&apos;s account</li>
          </ul>
          <p>
            You must notify us immediately at{" "}
            <a href="mailto:legal@directcampaign.org" className="text-foreground underline">
              legal@directcampaign.org
            </a>{" "}
            if you suspect unauthorized access to your account.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">4. Acceptable use</h2>
          <p>You may use the Service only for lawful political campaign and advocacy activities. You agree not to:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              Use the platform in violation of any applicable federal, state, or local law, including
              campaign finance laws, election laws, and voter contact regulations
            </li>
            <li>Upload voter data that you do not have a lawful right to use or process</li>
            <li>
              Use the platform to contact voters in ways that violate the Telephone Consumer
              Protection Act (TCPA), CAN-SPAM, or applicable state equivalents
            </li>
            <li>Harass, threaten, or intimidate voters or other individuals</li>
            <li>Attempt to gain unauthorized access to any other organization&apos;s data</li>
            <li>Reverse-engineer, decompile, or attempt to extract the source code of the platform</li>
            <li>
              Use the Service to store or transmit malicious code, engage in denial-of-service
              attacks, or otherwise attempt to disrupt the platform
            </li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">5. Your data</h2>
          <p>
            You retain full ownership of all voter data, campaign data, survey data, and other
            content you upload to the platform (&quot;Your Data&quot;). We process Your Data solely to
            provide the Service to you. We do not sell, license, or use Your Data for any purpose
            beyond service delivery.
          </p>
          <p>
            You are solely responsible for ensuring that Your Data was lawfully obtained and that
            your use of it through the platform complies with applicable law.
          </p>
          <p>
            You may export or request deletion of Your Data at any time by contacting{" "}
            <a href="mailto:legal@directcampaign.org" className="text-foreground underline">
              legal@directcampaign.org
            </a>
            . See our Privacy Policy for data retention details.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">6. Intellectual property</h2>
          <p>
            The Direct Campaign platform, including its software, design, and documentation, is
            owned by Direct Campaign and protected by copyright, trademark, and other intellectual
            property laws. These Terms do not grant you any ownership interest in the platform.
          </p>
          <p>
            You grant Direct Campaign a limited license to host, process, and display Your Data
            solely to the extent necessary to provide the Service.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">7. Availability and modifications</h2>
          <p>
            We will make reasonable efforts to keep the platform available, but we do not guarantee
            uninterrupted access. We may modify, suspend, or discontinue features of the Service
            with reasonable notice where practicable.
          </p>
          <p>
            We may update these Terms from time to time. Material changes will be communicated to
            administrators via email at least 14 days before taking effect. Continued use of the
            Service after that date constitutes acceptance of the updated Terms.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">8. Disclaimer of warranties</h2>
          <p>
            THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTY OF ANY KIND,
            EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
            PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE ERROR-FREE OR
            THAT DEFECTS WILL BE CORRECTED.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">9. Limitation of liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, DIRECT CAMPAIGN SHALL NOT BE LIABLE FOR ANY
            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR
            RELATED TO YOUR USE OF THE SERVICE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            OUR TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF THESE TERMS SHALL NOT EXCEED THE FEES
            PAID BY YOU IN THE THREE MONTHS PRECEDING THE CLAIM.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">10. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Direct Campaign and its officers, employees,
            and agents from any claims, damages, or costs (including reasonable attorneys&apos; fees)
            arising from your use of the Service, your violation of these Terms, or your violation
            of any third-party rights or applicable law.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">11. Governing law</h2>
          <p>
            These Terms are governed by the laws of the United States and the state in which Direct
            Campaign is incorporated, without regard to conflict of law principles. Any disputes
            shall be resolved in the courts of that jurisdiction.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">12. Contact</h2>
          <p>
            Questions about these Terms?{" "}
            <a href="mailto:legal@directcampaign.org" className="text-foreground underline">
              legal@directcampaign.org
            </a>
          </p>
        </section>

      </div>
    </div>
  );
}
